/**
 * Calibration — every score in a cycle on one page so ratings stay fair.
 * HR can move a person's band with a mandatory reason (logged + printed on
 * the appraisal PDF).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Scale, Loader2, ArrowLeft, ChevronRight, AlertTriangle, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../../../components/admin/billing/shared';
import { BAND_STYLE, Badge } from './shared';

const CalibrationPage = () => {
  const { authHeader } = useAuth();
  const [cycles, setCycles] = useState([]);
  const [cycleId, setCycleId] = useState('');
  const [data, setData] = useState({ cycle: null, rows: [], distribution: [], is_hr: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState(null); // { rid, band, reason }

  const load = useCallback(async (cid) => {
    setLoading(true);
    try {
      const qs = cid ? `?cycle_id=${cid}` : '';
      const [{ data: cl }, { data: cal }] = await Promise.all([
        axios.get(`${API}/performance/cycles`, { headers: authHeader() }),
        axios.get(`${API}/performance/calibration${qs}`, { headers: authHeader() }),
      ]);
      setCycles(cl.cycles || []);
      setData(cal);
      setCycleId((prev) => prev || cal.cycle?.id || '');
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(cycleId); }, [cycleId, load]);

  const saveOverride = async () => {
    if (!editing?.reason?.trim()) { toast.error('A reason is required'); return; }
    setBusy('override');
    try {
      await axios.post(`${API}/performance/reviews/${editing.rid}/band-override`, {
        band: editing.band, reason: editing.reason,
      }, { headers: authHeader() });
      toast.success('Band calibrated');
      setEditing(null);
      await load(cycleId);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const clearOverride = async (rid) => {
    setBusy(rid);
    try {
      await axios.delete(`${API}/performance/reviews/${rid}/band-override`, { headers: authHeader() });
      toast.success('Override removed — back to the computed band');
      await load(cycleId);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  return (
    <div className="space-y-5" data-testid="calibration-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/performance" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-1">
            <ArrowLeft size={14} /> Team scorecard
          </Link>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Scale size={20} className="text-teal-600" /> Calibration
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Every score in one view. Compare the band spread against the guide before publishing.
          </p>
        </div>
        <select
          className={`${inputCls} !w-auto min-w-[220px]`} value={cycleId}
          onChange={(e) => setCycleId(e.target.value)} data-testid="calibration-cycle-select"
        >
          {cycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="p-12 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div>
      ) : !data.cycle ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500">
          No cycle to calibrate yet.
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-3">Band distribution vs guide</h2>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-3" data-testid="band-distribution">
              {data.distribution.map((d) => {
                const off = d.actual_pct - d.guide_pct;
                return (
                  <div key={d.band} className={`rounded-lg border p-3 ${BAND_STYLE[d.band]}`} data-testid={`dist-${d.band}`}>
                    <div className="flex items-baseline justify-between">
                      <span className="text-lg font-semibold">{d.band}</span>
                      <span className="text-xs">{d.count} ppl</span>
                    </div>
                    <div className="text-[11px] mt-1">{d.label}</div>
                    <div className="text-[11px] mt-1 opacity-80">
                      {d.actual_pct}% vs guide {d.guide_pct}%
                      {Math.abs(off) >= 15 && (
                        <span className="ml-1 inline-flex items-center gap-0.5 font-semibold">
                          <AlertTriangle size={10} /> {off > 0 ? 'over' : 'under'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]" data-testid="calibration-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Staff</th>
                  <th className="text-left px-4 py-2">Manager</th>
                  <th className="text-right px-4 py-2">Goals</th>
                  <th className="text-right px-4 py-2">Behaviour</th>
                  <th className="text-right px-4 py-2">Final</th>
                  <th className="text-center px-4 py-2">Computed</th>
                  <th className="text-center px-4 py-2">Final band</th>
                  <th className="text-left px-4 py-2">Recommendation</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`calib-row-${r.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-900">{r.staff_name}</div>
                      <div className="text-xs text-slate-500">{r.staff_role || '—'}</div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{r.reporting_manager_name || '—'}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.goal_score}%</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.competency_score}%</td>
                    <td className="px-4 py-2.5 text-right font-mono font-semibold">{r.final_score}</td>
                    <td className="px-4 py-2.5 text-center text-xs text-slate-500">{r.computed_band}</td>
                    <td className="px-4 py-2.5 text-center">
                      <Badge className={BAND_STYLE[r.band]} testid={`calib-band-${r.id}`}>{r.band}</Badge>
                      {r.band_override && (
                        <div className="text-[10px] text-amber-700 mt-0.5" title={r.band_override.reason}>
                          calibrated
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">
                      {(r.manager_recommendation || '—').replace(/_/g, ' ')}
                      {r.increment_pct !== null && r.increment_pct !== undefined && (
                        <span className="text-emerald-700 font-medium"> · {r.increment_pct}%</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {data.is_hr && (
                        <>
                          <button
                            onClick={() => setEditing({ rid: r.id, band: r.band, reason: '' })}
                            className="text-xs text-teal-700 hover:text-teal-900 font-medium"
                            data-testid={`override-btn-${r.id}`}
                          >
                            Move band
                          </button>
                          {r.band_override && (
                            <button
                              onClick={() => clearOverride(r.id)} disabled={busy === r.id}
                              className="ml-2 text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-0.5"
                              data-testid={`clear-override-${r.id}`}
                            >
                              <RotateCcw size={11} /> reset
                            </button>
                          )}
                        </>
                      )}
                      <Link
                        to={`/admin/performance/reviews/${r.id}`}
                        className="ml-2 text-xs text-slate-500 hover:text-slate-800 inline-flex items-center"
                      >
                        open <ChevronRight size={12} />
                      </Link>
                    </td>
                  </tr>
                ))}
                {!data.rows.length && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-slate-500">No reviews in this cycle.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editing && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50" data-testid="override-modal">
          <div className="bg-white rounded-xl p-5 w-full max-w-md space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Calibration override</h3>
            <div>
              <label className="block text-xs text-slate-500 mb-1">New band</label>
              <select
                className={inputCls} value={editing.band}
                onChange={(e) => setEditing({ ...editing, band: e.target.value })}
                data-testid="override-band-select"
              >
                {['A', 'B', 'C', 'D', 'E'].map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Reason (required — printed on the appraisal letter)</label>
              <textarea
                rows={3} className={inputCls} value={editing.reason}
                onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
                data-testid="override-reason-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm text-slate-600">Cancel</button>
              <button
                onClick={saveOverride} disabled={busy === 'override'}
                className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-50"
                data-testid="save-override-btn"
              >
                {busy === 'override' ? <Loader2 size={15} className="animate-spin" /> : 'Save override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalibrationPage;
